import React, { useState, useRef, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import styles from './LegalPages.module.css';

interface Section {
  id?: string;
  title: string;
  content?: string;
  content2?: string;
  content3?: string;
  content4?: string;
  content5?: string;
  content6?: string;
  content7?: string;
  title2?: string;
  title3?: string;
  title4?: string;
  title5?: string;
}

interface ContentData {
  title: string;
  lastUpdated: string;
  intro: string;
  sections: Section[];
}

const Terms: React.FC = () => {
  const [language, setLanguage] = useState<'en' | 'ar'>('en');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.location.hash === '#money-back-guarantee') {
      document.getElementById('money-back-guarantee')?.scrollIntoView();
    }
  }, [language]);

  const handleLanguageChange = (newLanguage: 'en' | 'ar') => {
    setLanguage(newLanguage);
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const content: Record<'en' | 'ar', ContentData> = {
    en: {
      title: 'Terms and Conditions',
      lastUpdated: 'Last updated: 17/09/2026',
      intro: 'These Terms regulate your access to and use of the Services. Please review them carefully, as they contain important information regarding your legal rights. By accessing or using the Services, you agree to these Terms. If you do not agree with these Terms, please refrain from using the Services.',
      sections: [
        {
          title: 'Definitions',
          content: "For purposes of these Terms, (\"Company\", \"we\" or \"us\") refer to C Shaped Company. These Terms apply to our websites and any content, tools, features, and functionality offered through our Website, the C Shaped Engine (collectively, the \"Services\"). Please note, these Terms do not govern the use of the Company's APIs.",
          content2: "\"You\" and \"your\" refer to the user of the Services. If you use the Services on behalf of a company or other entity, \"you\" includes you and that entity, and you represent and warrant that:",
          content3: "1. You are an authorized representative of the entity with the authority to bind the entity to these Terms.\n2. You agree to these Terms on the entity's behalf.",
          content4: "By accessing or using the Site and/or the Services, you automatically accept and agree to the most recent version of this Agreement. If you do not accept and agree to this Agreement in its entirety, you are strictly prohibited from accessing or using the Site and/or the Services.",
        },
        {
          title: 'ELIGIBILITY',
          content: 'The site is offered only to users 18 years of age or older, or the age of majority in your jurisdiction, who have accepted this Agreement. By accessing or using the Site or the Services, you represent and warrant that you meet these eligibility requirements and agree to comply with all applicable laws.',
        },
        {
          title: 'Not Intended for Medical Advice',
          content: 'C Shaped Company does not provide medical advice or any service requiring a medical license. Information accessed through the Site is not intended for diagnosis, evaluation, or treatment, and C Shaped Company makes no representations or warranties regarding the accuracy or appropriateness of such information.',
        },
        {
          title: 'DEFINITION OF THE SERVICE',
          content: 'C Shaped Company provides access to:',
          content2: '- Its systems - Digital content (collectively, the "Service")',
          content3: 'The Service includes software systems, search queries, fonts, and digital content provided by C Shaped Company and its licensors, including third-party publishers. These are licensed to you by C Shaped Company for use only under these terms and conditions.The Service includes software systems, search queries, fonts, and digital content provided by C Shaped Company and its licensors, including third-party publishers. These are licensed to you by C Shaped Company for use only under these terms and conditions.',
          content4: 'Software and Service updates from C Shaped Company may not include all existing features or new features for newer browser versions or devices. The terms of this License will govern any Software updates unless such updates come with a separate license, in which case the terms of that license will govern.',
        },
        {
          title: 'YOUR AGREEMENT',
          content: 'By clicking the checkbox labeled "Yes, I agree to the Terms and Conditions" when you first access the Service, you enter into a binding legal agreement as detailed in this Agreement and its accompanying Privacy Policy. If you do not agree to these terms, do not access the C Shaped Company apps or use the Service.',
          content2: 'If you have been invited to access any Service by a third party (such as a university), your use of our Services is also subject to any agreements between the third party and C Shaped Company, as well as any instructions or restrictions requested by such third party.',
        },
        {
          title: 'PRIVACY',
          content: 'C Shaped Company respects the privacy of others. The company\'s policies on the collection and use of your personal information are detailed in our Privacy Policy, which you should review each time you access or use the Site or the Services.',
        },
        {
          title: 'USER ACCOUNT ACTIVITY AND INFORMATION',
          content: 'You are responsible for all activity that occurs under your User Account and must not allow any third party to use your account. Protect your account details, including your login name and password. Maintain accurate, complete, and up-to-date.',
        },
        {
          title: 'RESERVATION OF RIGHTS',
          content: 'Nothing in this Agreement limits C Shaped Company\'s rights, title, or interests in the Site, Services, User Materials, or any derivatives thereof.',
        },
        {
          title: 'SERVICE FEES',
          content: 'The Service is accessible through a service fee that credits your account. We will display the fee details before you purchase. Service fees may change, and such changes will apply to subsequent renewals or new purchases. Different credit packages are available.',
        },
        {
          title: 'PROVISION OF SERVICE',
          content: 'We may need to suspend the Service for the following reasons:',
          content2: '- To fix technical issues or apply new software versions. - To comply with legal or regulatory changes.',
        },
        {
          title: 'PAYMENT',
          content: 'Payment for your Subscription is taken immediately upon order acceptance and at regular intervals thereafter until you cancel your subscription. You will have access to the Service once you activate your Subscription by submitting payment details and accepting the Terms and Conditions.',
          content2: 'If you believe you were incorrectly charged or experience a problem with the Service, contact support@c-shaped.com. You may cancel your subscription at any time; access continues until the end of the paid period unless that purchase is refunded.',
          content3: 'Refunds are available under the Money-Back Guarantee below and for service problems on our side. Nothing in these Terms limits any refund or other consumer rights available under applicable law.',
        },
        {
          id: 'money-back-guarantee',
          title: 'MONEY-BACK GUARANTEE',
          content: 'If you use C-Shaped to prepare for the Saudi Medical Licensing Examination (SMLE) or Saudi Dental Licensing Examination (SDLE), sit the exam, and receive an official result showing that you did not pass, you are entitled to a full refund of the C-Shaped subscription and credit purchases used to prepare for that exam attempt.',
          content2: 'To request your refund, email support@c-shaped.com from your registered email address. Include the exam name and date, your official result showing that you did not pass, and the receipts or transaction references for the purchases you want refunded. Your name, exam date, and result must remain visible; you may redact unrelated personal information and identification numbers.',
          content3: 'After verifying your result and purchases, we will issue the refund within 14 days to the original payment method. Your bank or payment provider may need additional time to post the refund. A money-back refund will not be replaced with account credits without your agreement. Refunded purchases cannot be refunded again, and any remaining access or credits from those purchases will be removed.',
          content4: 'The guarantee covers amounts actually paid to C-Shaped for preparation for the unsuccessful attempt. It does not reimburse exam registration fees or other third-party expenses. This is a refund promise if you do not pass, not a promise of a passing score. Your statutory consumer rights remain unaffected.',
        },
        {
          title: 'OWNERSHIP OF FEEDBACK',
          content: 'We welcome feedback on our Services. Any feedback, comments, or suggestions you provide become the sole property of C Shaped Company, and the company may use them without notice or compensation to you. You assign all rights to any feedback to C Shaped Company.',
        },
        {
          title: 'YOUR INFORMATION',
          content: 'You agree to provide accurate, current, and complete information required to register with the Service and at other points as needed while using the Service ("Registration Data"). You also agree to maintain and update your Registration Data to keep it accurate, current, and complete. C Shaped Company may terminate your access to the Service if any information you provide is false, inaccurate, or incomplete.',
        },
        {
          title: 'DATA',
          content: 'You grant us a non-exclusive, royalty-free, perpetual, worldwide license to host, use, copy, store, collect, process, transmit, retain, publish, and display your data as necessary for the provision of the Service or to fulfill our obligations. You also grant us a non-exclusive, royalty-free, perpetual, worldwide license to use your data anonymously. We may aggregate this anonymized data with data from other customers and third parties. Ownership of such anonymized and aggregated data, and all related intellectual property rights, will belong to us or vest in us upon creation.',
        },
        {
          title: 'TERMINATION OF YOUR RIGHT TO USE THE SERVICE',
          content: 'C Shaped Company may terminate your use of the Service for breach of this Agreement, subject to the Money-Back Guarantee and applicable consumer rights. If Company terminates the Agreement for other reasons, a refund of fees paid by you will be issued. Upon termination, your right to use the Service and all associated license rights will immediately cease. You will no longer have access to the Service, and all rights to use the Service will end. Termination does not relieve you of any obligations incurred before termination or limit any liability you may have to C Shaped Company.',
        },
        {
          title: 'DISCLAIMERS AND LIMITATIONS',
          title2: 'Changes to the Website',
          content: 'We may update the content on this Website periodically, but the content may not always be complete or up-to-date. We are not obligated to update any material on the website.',
          title3: 'Disclaimer About System Delays',
          content2: 'You acknowledge that the Site may experience limitations, delays, and other issues inherent in third-party communication networks and facilities outside of C Shaped Company\'s control. Consequently, C Shaped Company is not responsible for and expressly disclaims any delays, failures, losses, injuries, liabilities, or damages associated with the Site due to system delays, downtimes, interruptions, or other issues outside our control, including scheduled maintenance or network failures.',
          title4: 'Information About You and Your Visits to the Website',
          content3: 'All information collected on this website is subject to our Privacy Policy. By using the Website, you consent to all actions taken by us concerning your information in compliance with the Privacy Policy.',
          title5: 'Search Results and Citations',
          content4: 'C-shaped does not claim copyright or ownership of any of the citations or quotations provided with each question.',
        },
        {
          title: 'COMPLAINT HANDLING AND RESOLUTION PROCESS',
          content: 'We are dedicated to handling user complaints with promptness and fairness. You can submit your complaints through our designated contact channels, providing as much detail as possible. Upon receiving your complaint, we will acknowledge it within 48 hours and begin an investigation. Our goal is to resolve most issues within 7 business days while keeping you informed of the progress. If additional time is needed, we will notify you with an updated timeline. We are committed to ensuring a transparent and satisfactory resolution to every complaint.',
        },
        {
          title: 'HOW TO CONTACT US',
          content: 'If you have any questions about the Site or this Agreement, please contact us at ',
          content2: 'support@c-shaped.com',
          content3: ' with the subject line "Website Question." Please note that this contact information may change from time to time through updates to this Agreement.',
        },
      ]
    },
    ar: {
      title: 'الشروط والأحكام',
      lastUpdated: 'آخر تحديث: 17/09/2026',
      intro: 'تنظم هذه الشروط وصولك إلى الخدمات واستخدامها. يرجى مراجعتها بعناية، حيث تحتوي على معلومات مهمة بشأن حقوقك القانونية. من خلال الوصول إلى الخدمات أو استخدامها، فإنك توافق على هذه الشروط. إذا كنت لا توافق على هذه الشروط، يرجى الامتناع عن استخدام الخدمات.',
      sections: [
        {
          title: 'التعريفات',
          content: 'لأغراض هذه الشروط ، تشير ("الشركة" أو "نحن" أو "نا") إلى شركة سي شيبيد. تنطبق هذه الشروط على مواقعنا على الويب وأي محتوى أو أدوات أو ميزات أو وظائف يتم تقديمها من خلال موقعنا على الويب ، محرك سي شيبيد (يُشار إليها مجتمعةً باسم "الخدمات"). يرجى ملاحظة أن هذه الشروط لا تحكم استخدام واجهات برمجة تطبيقات الشركة.',
          content2: 'يشير مصطلحا "أنت" و "الخاص بك" إلى مستخدم الخدمات. إذا كنت تستخدم الخدمات نيابةً عن شركة أو كيان آخر ، فإن "أنت" تشملك أنت وهذا الكيان ، وأنت تقر وتضمن ما يلي:',
          content3: '1. أنت ممثل مخول للكيان الذي يتمتع بصلاحية إلزام الكيان بهذه الشروط.\n 2. أنت توافق على هذه الشروط نيابةً عن الكيان.',
          content4: 'بالوصول إلى الموقع أو استخدامه و / أو الخدمات ، فإنك توافق تلقائيًا على أحدث إصدار من هذه الاتفاقية. إذا كنت لا تقبل هذه الاتفاقية وتوافق عليها بالكامل ، فأنت ممنوع منعا باتًا من الوصول إلى الموقع أو استخدامه و / أو الخدمات.'
        },
        {
          title: 'الأهلية',
          content: 'يتم تقديم الموقع فقط للمستخدمين الذين تبلغ أعمارهم 18 عامًا أو أكبر ، أو سن الرشد في ولايتك القضائية ، والذين وافقوا على هذه الاتفاقية. من خلال الوصول إلى الموقع أو الخدمات أو استخدامها ، فإنك تقر وتضمن أنك تستوفي متطلبات الأهلية هذه وتوافق على الامتثال لجميع القوانين المعمول بها.'
        },
        {
          title: 'غير مخصص للمشورة الطبية',
          content: 'لا تقدم شركة سي شيبيد المشورة الطبية أو أي خدمة تتطلب ترخيصًا طبيًا. لا يُقصد من المعلومات التي يتم الوصول إليها من خلال الموقع أن تكون تشخيصًا أو تقييمًا أو علاجًا ، ولا تقدم شركة سي شيبيد أي إقرارات أو ضمانات فيما يتعلق بدقة أو ملاءمة هذه المعلومات.'
        },
        {
          title: 'تعريف الخدمة',
          content: 'تتيح شركة سي شيبيد الوصول إلى:',
          content2: '- أنظمتها - المحتوى الرقمي (يُشار إليها مجتمعةً باسم "الخدمة")',
          content3: 'تتضمن الخدمة أنظمة برامج واستعلامات بحث وخطوطًا ومحتوى رقميًا توفره شركة سي شيبيد ومُرخصيها ، بما في ذلك ناشري الطرف الثالث. تم ترخيصها لك من قبل شركة سي شيبيد للاستخدام فقط بموجب هذه الشروط والأحكام. تتضمن الخدمة أنظمة برامج واستعلامات بحث وخطوطًا ومحتوى رقميًا توفره شركة سي شيبيد ومُرخصيها ، بما في ذلك ناشري الطرف الثالث. تم ترخيصها لك من قبل شركة سي شيبيد للاستخدام فقط بموجب هذه الشروط والأحكام.',
          content4: 'قد لا تتضمن تحديثات البرامج والخدمات من شركة سي شيبيد جميع الميزات الحالية أو الميزات الجديدة لإصدارات أو أجهزة المتصفح الأحدث. ستخضع شروط الترخيص هذه لأي تحديثات للبرامج ما لم تكن هذه التحديثات مصحوبة بترخيص منفصل ، وفي هذه الحالة ستخضع شروط ذلك الترخيص.',
        },
        {
          title: 'اتفاقيتك',
          content: 'بالنقر على مربع الاختيار المسمى "نعم ، أوافق على الشروط والأحكام" عند دخولك إلى الخدمة لأول مرة ، فإنك تدخل في اتفاقية قانونية ملزمة على النحو المفصل في هذه الاتفاقية وسياسة الخصوصية المصاحبة لها. إذا كنت لا توافق على هذه الشروط ، فلا تدخل إلى تطبيقات C Shaped Company أو تستخدم الخدمة.',
          content2: 'إذا تمت دعوتك للوصول إلى أي خدمة من قبل طرف ثالث (مثل جامعة) ، فإن استخدامك لخدماتنا يخضع أيضًا لأي اتفاقيات بين الطرف الثالث وشركة سي شيبيد ، بالإضافة إلى أي تعليمات أو قيود يطلبها هذا الطرف الثالث.',
        },
        {
          title: 'الخصوصية',
          content: 'تحترم شركة سي شيبيد خصوصية الآخرين. تم تفصيل سياسات الشركة بشأن جمع واستخدام معلوماتك الشخصية في سياسة الخصوصية الخاصة بنا ، والتي يجب عليك مراجعتها في كل مرة تدخل فيها أو تستخدم الموقع أو الخدمات.',
        },
        {
          title: 'نشاط معلومات حساب المستخدم',
          content: 'أنت مسؤول عن جميع الأنشطة التي تحدث بموجب حساب المستخدم الخاص بك ويجب ألا تسمح لأي طرف ثالث باستخدام حسابك. احمِ تفاصيل حسابك ، بما في ذلك اسم المستخدم وكلمة المرور. حافظ على معلومات دقيقة وكاملة وحديثة في حساب المستخدم الخاص بك. أخطر شركة سي شيبيد على الفور إذا كنت تشك في استخدام غير مصرح به لحسابك. يحق للشركة الوصول إلى المعلومات الموجودة في حسابك واستخدامها لأداء التزاماتها وحقوقها بموجب هذه الاتفاقية.',
        },
        {
          title: 'حفظ الحقوق',
          content: 'لا يوجد في هذه الاتفاقية ما يحد من حقوق أو ملكية أو مصالح شركة سي شيبيد في الموقع أو الخدمات أو مواد المستخدم أو أي مشتقات منها.',
        },
        {
          title: 'رسوم الخدمة',
          content: 'يمكن الوصول إلى الخدمة من خلال رسوم خدمة يتم من خلالها إضافة رصيد إلى حسابك. سنعرض تفاصيل الرسوم قبل الشراء. قد تتغير رسوم الخدمة ، وتنطبق هذه التغييرات على التجديدات اللاحقة أو عمليات الشراء الجديدة. تتوفر باقات ائتمان مختلفة.',
        },
        {
          title: 'توفير الخدمة',
          content: 'قد نحتاج إلى تعليق الخدمة للأسباب التالية:',
          content2: '- لإصلاح المشكلات الفنية أو تطبيق إصدارات برامج جديدة. - للامتثال للتغييرات القانونية أو التنظيمية.',
        },
        {
          title: 'الدفع',
          content: 'يتم سداد مدفوعات اشتراكك فور قبول الطلب وعلى فترات منتظمة بعد ذلك حتى تقوم بإلغاء اشتراكك. ستتمكن من الوصول إلى الخدمة بمجرد تفعيل اشتراكك من خلال إرسال تفاصيل الدفع وقبول الشروط والأحكام.',
          content2: 'إذا كنت تعتقد أنه تم تحصيل رسوم منك بشكل غير صحيح ، فيرجى الاتصال بنا على support@c-shaped.com. يمكنك إلغاء اشتراكك في أي وقت ، وسيستمر الوصول حتى نهاية الفترة الحالية.',
          content3: 'إذا واجهت مشكلة في الخدمة، يرجى التواصل معنا على support@c-shaped.com. تتوفر المبالغ المستردة بموجب ضمان استرداد المال أدناه وللمشكلات التي تكون من جانبنا. لا تحد هذه الشروط من أي حقوق في الاسترداد أو غيرها من حقوق المستهلك المقررة بموجب الأنظمة المعمول بها. وفي حال استرداد قيمة الاشتراك، ينتهي الوصول المرتبط به.',
        },
        {
          id: 'money-back-guarantee',
          title: 'ضمان استرداد المال',
          content: 'إذا استخدمت سي شيبيد للتحضير لاختبار الرخصة السعودية لمزاولة مهنة الطب البشري (SMLE) أو طب الأسنان (SDLE)، وأديت الاختبار، وأظهرت نتيجتك الرسمية أنك لم تجتز الاختبار، فيحق لك استرداد كامل قيمة اشتراكات وأرصدة سي شيبيد التي استخدمتها للتحضير لتلك المحاولة.',
          content2: 'لطلب الاسترداد، أرسل رسالة إلى support@c-shaped.com من بريدك الإلكتروني المسجل لدينا، تتضمن اسم الاختبار وتاريخه، والنتيجة الرسمية التي توضح عدم الاجتياز، وإيصالات أو أرقام عمليات الشراء المطلوب استردادها. يجب أن يظل اسمك وتاريخ الاختبار والنتيجة ظاهرين، ويمكنك إخفاء البيانات الشخصية غير ذات الصلة وأرقام الهوية.',
          content3: 'بعد التحقق من النتيجة وعمليات الشراء، سنصدر الاسترداد خلال 14 يومًا إلى وسيلة الدفع الأصلية. قد يحتاج البنك أو مزود الدفع إلى وقت إضافي لإظهار المبلغ في حسابك. لن نستبدل المبلغ المسترد برصيد في حسابك دون موافقتك. لا يمكن استرداد عملية الشراء نفسها أكثر من مرة، وسيُلغى أي وصول أو رصيد متبقٍ مرتبط بعمليات الشراء المستردة.',
          content4: 'يشمل الضمان المبالغ المدفوعة فعليًا إلى سي شيبيد للتحضير للمحاولة التي لم تُجتز. ولا يشمل رسوم التسجيل في الاختبار أو أي نفقات لأطراف أخرى. هذا تعهد باسترداد المال عند عدم الاجتياز، وليس وعدًا بالحصول على درجة النجاح. ولا يؤثر هذا الضمان في حقوقك النظامية كمستهلك.',
        },
        {
          title: 'حقوق ملكية التغذية الراجعة',
          content: 'نرحب بتعليقاتك على خدماتنا. تصبح أي ملاحظات أو تعليقات أو اقتراحات تقدمها ملكية حصرية لشركة سي شيبيد ، ويجوز للشركة استخدامها دون إشعار لك أو تعويض لك. أنت تعين جميع الحقوق في أي ملاحظات إلى شركة سي شيبيد.',
        },
        {
          title: 'معلوماتك',
          content: 'أنت توافق على تقديم معلومات دقيقة وحالية وكاملة مطلوبة للتسجيل في الخدمة وفي نقاط أخرى حسب الحاجة أثناء استخدام الخدمة ("بيانات التسجيل"). كما أنك توافق على الاحتفاظ ببيانات التسجيل الخاصة بك وتحديثها للحفاظ عليها دقيقة وحديثة وكاملة. يجوز لشركة سي شيبيد إنهاء وصولك إلى الخدمة إذا كانت أي معلومات تقدمها خاطئة أو غير دقيقة أو غير كاملة.',
        },
        {
          title: 'البيانات',
          content: "أنت تمنحنا ترخيصًا غير حصري ، خاليًا من حقوق الملكية ، ودائمًا ، وعالميًا لاستضافة بياناتك واستخدامها ونسخها وتخزينها وجمعها ومعالجتها ونقلها والاحتفاظ بها ونشرها وعرضها حسب الضرورة لتقديم الخدمة أو للوفاء بالتزاماتنا. كما أنك تمنحنا ترخيصًا غير حصري وخالي من حقوق الملكية ودائمًا وعالميًا لاستخدام بياناتك بشكل مجهول. يجوز لنا تجميع هذه البيانات المجهولة مع بيانات من عملاء آخرين وأطراف ثالثة. ستكون ملكية هذه البيانات المجهولة والمجمعة ، وجميع حقوق الملكية الفكرية ذات الصلة ، لنا أو تؤول إلينا عند الإنشاء.",
        },
        {
          title: 'إنهاء حقك في استخدام الخدمة',
          content: 'يجوز لشركة سي شيبيد إنهاء استخدامك للخدمة لخرق هذه الاتفاقية، مع مراعاة ضمان استرداد المال وحقوق المستهلك المقررة نظامًا. إذا أنهت الشركة الاتفاقية لأسباب أخرى ، فسيتم إصدار رد المبالغ المدفوعة بواسطتك. عند الإنهاء ، سينتهي حقك في استخدام الخدمة وجميع حقوق الترخيص المرتبطة بها على الفور. لن تتمكن من الوصول إلى الخدمة بعد الآن ، وستنهي جميع حقوق استخدام الخدمة. لا يعفيك الإنهاء من أي التزامات تم تكبدها قبل الإنهاء أو يحد من أي مسؤولية قد تكون لديك تجاه شركة سي شيبيد.',
        },
        {
          title: 'إخلاء المسؤولية والقيود',
          title2: 'تغييرات في الموقع',
          content: 'يجوز لنا تحديث المحتوى على موقع الويب هذا بشكل دوري ، ولكن قد لا يكون المحتوى كاملاً أو محدثًا دائمًا. لسنا ملزمين بتحديث أي مادة على موقع الويب.',
          title3: 'إخلاء المسؤولية عن تأخيرات النظام',
          content2: 'أنت تقر بأن الموقع قد يتعرض لقيود وتأخيرات ومشكلات أخرى متأصلة في شبكات ومرافق الاتصالات التابعة لجهات خارجية خارجة عن سيطرة شركة سي شيبيد. وبالتالي ، فإن شركة سي شيبيد ليست مسؤولة عن وتخلي مسؤوليتها صراحةً عن أي تأخيرات أو إخفاقات أو خسائر أو إصابات أو التزامات أو أضرار مرتبطة بالموقع بسبب تأخيرات النظام أو التوقفات أو الانقطاعات أو المشكلات الأخرى الخارجة عن سيطرتنا ، بما في ذلك الصيانة المجدولة أو أعطال الشبكة.',
          title4: 'معلومات عنك وزيارتك إلى الموقع',
          content3: 'تخضع جميع المعلومات التي يتم جمعها على موقع الويب هذا لسياسة الخصوصية الخاصة بنا. باستخدام موقع الويب ، فإنك توافق على جميع الإجراءات التي نتخذها فيما يتعلق بمعلوماتك امتثالًا لسياسة الخصوصية.',
          title5: 'نتائج البحث والاقتباسات',
          content4: 'لا تدعي شركة سي شيبيد حقوق الطبع والنشر أو ملكية أي من الاقتباسات أو النصوص المرجعية المقدمة مع كل سؤال.',
        },
        {
          title: 'عملية التعامل مع الشكاوى وحلها',
          content: 'نحن ملتزمون بالتعامل مع شكاوى المستخدمين بسرعة وعدالة. يمكنك تقديم شكاواك من خلال قنوات الاتصال المخصصة لدينا ، مع تقديم أكبر قدر من التفاصيل. عند تلقي شكواك ، سنؤكد استلامها في غضون 48 ساعة وسنبدأ في التحقيق. هدفنا هو حل معظم المشكلات في غضون 7 أيام عمل مع إبقائك على اطلاع بالتقدم المحرز. إذا كان هناك حاجة لوقت إضافي ، فسنخطرك بجدول زمني محدث. نحن ملتزمون بضمان حل شفاف ومُرضٍ لكل شكوى.',
        },
        {
          title: 'كيفية الاتصال بنا',
          content: 'إذا كان لديك أي أسئلة حول الموقع أو هذه الاتفاقية ، يرجى الاتصال بنا على ',
          content2: 'support@c-shaped.com',
          content3: ' مع عنوان الموضوع "سؤال حول الموقع". يرجى ملاحظة أن معلومات الاتصال هذه قد تتغير من وقت لآخر من خلال تحديثات هذه الاتفاقية.',
        },
      ]
    }
  };

  const currentContent = content[language];

  return (
    <div className={styles.legalPage}>
      <Header />
      <main className={`${styles.mainContent} ${language === 'ar' ? styles.rtl : styles.ltr}`}>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.headerTop}>
              <h1 className={styles.title}>{currentContent.title}</h1>
              <div className={styles.languageDropdown} ref={dropdownRef}>
                <button
                  className={styles.languageToggle}
                  onClick={toggleDropdown}
                  aria-expanded={isDropdownOpen}
                  aria-haspopup="true"
                >
                  <svg className={styles.globeIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" strokeWidth="2" />
                    <path d="M2 12h20" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span className={styles.selectedLanguage}>
                    {language === 'en' ? 'English' : 'العربية'}
                  </span>
                  <svg className={`${styles.chevronIcon} ${isDropdownOpen ? styles.open : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                {isDropdownOpen && (
                  <div className={styles.dropdownMenu}>
                    <div className={styles.dropdownHeader}>
                      Choose your language:
                    </div>
                    <button
                      className={`${styles.dropdownItem} ${language === 'ar' ? styles.selected : ''}`}
                      onClick={() => handleLanguageChange('ar')}
                    >
                      <span>العربية</span>
                      {language === 'ar' && (
                        <svg className={styles.checkIcon} width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                    <button
                      className={`${styles.dropdownItem} ${language === 'en' ? styles.selected : ''}`}
                      onClick={() => handleLanguageChange('en')}
                    >
                      <span>English</span>
                      {language === 'en' && (
                        <svg className={styles.checkIcon} width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <p className={styles.lastUpdated}>{currentContent.lastUpdated}</p>
          </div>

          <div className={styles.content}>
            <p className={styles.intro}>{currentContent.intro}</p>

            {currentContent.sections.map((section, index) => (
              <div key={index} id={section.id} className={styles.section} style={section.id ? { scrollMarginTop: "120px" } : undefined}>
                <h2 className={styles.sectionTitle}>{section.title}</h2>
                {/* For sections with subsections, don't render main content separately as it belongs to title2 */}
                {!section.title2 && section.content && <p className={styles.sectionContent}>{section.content}</p>}

                {section.title2 && (
                  <>
                    <h3 className={styles.sectionSubtitle}>{section.title2}</h3>
                    {section.content && <p className={styles.sectionContent}>{section.content}</p>}
                  </>
                )}

                {section.title3 && (
                  <>
                    <h3 className={styles.sectionSubtitle}>{section.title3}</h3>
                    {section.content2 && <p className={styles.sectionContent}>{section.content2}</p>}
                  </>
                )}

                {section.title4 && (
                  <>
                    <h3 className={styles.sectionSubtitle}>{section.title4}</h3>
                    {section.content3 && <p className={styles.sectionContent}>{section.content3}</p>}
                  </>
                )}

                {section.title5 && (
                  <>
                    <h3 className={styles.sectionSubtitle}>{section.title5}</h3>
                    {section.content4 && <p className={styles.sectionContent}>{section.content4}</p>}
                  </>
                )}

                {/* Render any remaining content that doesn't have a subtitle */}
                {!section.title3 && section.content2 && <p className={styles.sectionContent}>{section.content2}</p>}
                {!section.title4 && section.content3 && <p className={styles.sectionContent}>{section.content3}</p>}
                {!section.title5 && section.content4 && <p className={styles.sectionContent}>{section.content4}</p>}
                {section.content6 && <p className={styles.sectionContent}>{section.content6}</p>}
                {section.content7 && <p className={styles.sectionContent}>{section.content7}</p>}
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Terms; 